import { useState } from 'react';
import { ArrowRight, ShieldCheck } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { Input } from '../components/Input';
import { Select } from '../components/Select';
import { Alert } from '../components/Alert';
import { Logo } from '../components/Logo';

interface OnboardingProps {
  onSuccess: (newOrgId: string) => void;
  onCancel?: () => void;
}

export function OnboardingPage({ onSuccess, onCancel }: OnboardingProps) {
  const { session, refreshSession } = useAuth();
  const [displayName, setDisplayName] = useState('');
  const [legalName, setLegalName] = useState('');
  const [country, setCountry] = useState('FR');
  const [billingEmail, setBillingEmail] = useState(session?.principal.email || '');
  const [slug, setSlug] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!displayName.trim()) {
      setError('Organisation display name is required');
      return;
    }
    setError(null);
    setLoading(true);

    try {
      const org = await api.orgs.create({
        display_name: displayName.trim(),
        legal_name: legalName.trim() || undefined,
        country: country || 'FR',
        billing_email: billingEmail.trim() || undefined,
        slug: slug.trim() || undefined,
      });

      // Switch context to newly created organisation
      await api.auth.switchOrg(org.id);
      await refreshSession();
      onSuccess(org.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create organisation');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: 600, margin: '0 auto', padding: 'var(--space-6) 0', display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Logo variant="lockup" size={32} tagline="NEW TENANT REGISTRATION" />
        {onCancel && (
          <Button variant="ghost" size="sm" onClick={onCancel}>
            Back
          </Button>
        )}
      </div>

      <div>
        <h1 style={{ fontSize: 'var(--text-2xl)', fontWeight: 'var(--fw-bold)' }}>Create an Organisation</h1>
        <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)', marginTop: 4 }}>
          Every service in CYRN belongs to an Organisation. You will be registered as the first Owner.
        </p>
      </div>

      {error && (
        <Alert tone="danger" title="Registration failed">
          {error}
        </Alert>
      )}

      <Card padding="var(--space-6)">
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          <Input
            label="Organisation Display Name *"
            placeholder="e.g. Acme Studio, CyberSec Labs"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            disabled={loading}
            required
            hint="The public name displayed in dashboards and team invites."
          />

          <Input
            label="Legal Entity Name (Optional)"
            placeholder="e.g. ACME SAS"
            value={legalName}
            onChange={(e) => setLegalName(e.target.value)}
            disabled={loading}
            hint="Required for official French & EU B2B invoicing compliance."
          />

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)' }}>
            <Select
              label="Country / Jurisdiction *"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              disabled={loading}
              options={[
                { label: 'France (FR)', value: 'FR' },
                { label: 'Germany (DE)', value: 'DE' },
                { label: 'United Kingdom (GB)', value: 'GB' },
                { label: 'United States (US)', value: 'US' },
                { label: 'Switzerland (CH)', value: 'CH' },
                { label: 'Belgium (BE)', value: 'BE' },
                { label: 'Netherlands (NL)', value: 'NL' },
                { label: 'Spain (ES)', value: 'ES' },
                { label: 'Italy (IT)', value: 'IT' },
                { label: 'Other Jurisdiction', value: 'ZZ' },
              ]}
            />

            <Input
              label="Billing Email (Optional)"
              type="email"
              placeholder="billing@acme.com"
              value={billingEmail}
              onChange={(e) => setBillingEmail(e.target.value)}
              disabled={loading}
              hint="Invoices and payment notices are routed here."
            />
          </div>

          <Input
            label="Custom URL Slug (Optional)"
            placeholder="acme-corp"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            disabled={loading}
            mono
            hint="Permanent identifier. Leave blank to generate automatically."
          />

          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 10,
              padding: '12px',
              background: 'var(--bg-inset)',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--border-subtle)',
              marginTop: 4,
            }}
          >
            <ShieldCheck size={16} color="var(--accent)" style={{ flexShrink: 0, marginTop: 2 }} />
            <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
              <strong>Tenancy Guarantee:</strong> All hosting deployments, domains, and security monitoring tokens are strictly isolated to this organisation boundary (SPEC-01).
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 'var(--space-2)' }}>
            {onCancel && (
              <Button variant="secondary" onClick={onCancel} disabled={loading}>
                Cancel
              </Button>
            )}
            <Button
              type="submit"
              variant="primary"
              disabled={loading || !displayName.trim()}
              icon={<ArrowRight size={16} />}
            >
              {loading ? 'Creating...' : 'Create Organisation & Proceed'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
